import React, { useState, useCallback, useContext } from 'react';
import {
  Box,
  Text,
  Flex,
  Input,
  FormControl,
  FormLabel,
  List,
  ListItem,
  Image,
  useToast,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { brandApi } from '../../../services/api/brand';
import { BrandSearchResultDto } from '@foodfluence/shared';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';

interface AddBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * When set, the selected brand is persisted via this callback (e.g. marketplace profile)
   * instead of the main influencer/UGC `brands` list.
   */
  addBrandOverride?: (brandName: string) => Promise<void>;
}

const AddBrandModal: React.FC<AddBrandModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  addBrandOverride,
}) => {
  const { influencerData } = useContext(InfluencerDataContext);
  const [brandName, setBrandName] = useState('');
  const [suggestions, setSuggestions] = useState<BrandSearchResultDto[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandSearchResultDto | null>(null);
  const [isBrandSelected, setIsBrandSelected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const fetchBrandSuggestions = async (input: string) => {
    if (input.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const results = await brandApi.searchBrands(input);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching brand suggestions:', error);
      setSuggestions([]);
    }
  };

  const debouncedFetchBrands = useCallback(debounce(fetchBrandSuggestions, 300), []);

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrandName(value);
    setIsBrandSelected(false);
    setSelectedBrand(null);
    debouncedFetchBrands(value);
  };

  const handleSuggestionClick = (brand: BrandSearchResultDto) => {
    setSelectedBrand(brand);
    setBrandName(brand.name);
    setIsBrandSelected(true);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleCancel = () => {
    setBrandName('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedBrand(null);
    setIsBrandSelected(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!isBrandSelected || !selectedBrand) {
      toast({
        title: "Please select a brand",
        description: "Choose a brand from the suggestions",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      if (addBrandOverride) {
        await addBrandOverride(selectedBrand.name);
      } else {
        if (!influencerData?.id) {
          throw new Error('Missing influencer');
        }
        await brandApi.addBrandToInfluencer(selectedBrand.name, influencerData.id);
      }

      toast({
        title: 'Brand added',
        description: addBrandOverride
          ? `${selectedBrand.name} has been added to your marketplace profile`
          : `${selectedBrand.name} has been added to your profile`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      if (onSuccess) {
        onSuccess();
      }

      // Reset form and close
      setBrandName('');
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedBrand(null);
      setIsBrandSelected(false);
      onClose();
    } catch (error: any) {
      if (error?.message === '__BRAND_DUPLICATE__') {
        return;
      }
      console.error('Error adding brand:', error);
      toast({
        title: "Error adding brand",
        description: error.message || "Please try again",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Add brand"
      cancelLabel="Cancel"
      onCancel={handleCancel}
      isCancelDisabled={isSubmitting}
      primary={{
        label: 'Submit',
        onClick: () => void handleSubmit(),
        tone: 'lime',
        isDisabled: !isBrandSelected || isSubmitting,
        isLoading: isSubmitting,
      }}
    >
      {/* minH leaves room for the suggestion dropdown inside the sheet */}
      <Box minH="240px">
        <FormControl position="relative">
          <FormLabel>Brand name</FormLabel>
          <Flex align="center" position="relative">
            {selectedBrand && selectedBrand.icon && (
              <Box position="absolute" left={3} zIndex={2}>
                <Image
                  src={selectedBrand.icon}
                  alt={selectedBrand.name}
                  boxSize="24px"
                  borderRadius="6px"
                  objectFit="contain"
                />
              </Box>
            )}
            <Input
              placeholder="Start typing brand name..."
              value={brandName}
              onChange={handleBrandNameChange}
              borderColor={isBrandSelected ? ff.ink : undefined}
              pl={selectedBrand && selectedBrand.icon ? '44px' : '16px'}
            />
          </Flex>

          {showSuggestions && suggestions.length > 0 && (
            <List
              position="absolute"
              top="100%"
              left={0}
              right={0}
              bg="white"
              boxShadow="0 8px 24px rgba(14, 14, 14, 0.08)"
              borderRadius="14px"
              maxH="300px"
              overflowY="auto"
              zIndex={1000}
              mt={2}
              border="1px solid"
              borderColor={ff.line}
            >
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  minH="52px"
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: ff.grey }}
                  borderTop={index === 0 ? 'none' : '1px solid'}
                  borderColor={ff.line}
                  onClick={() => handleSuggestionClick(suggestion)}
                  display="flex"
                  alignItems="center"
                  gap={3}
                >
                  {suggestion.icon && (
                    <Image
                      src={suggestion.icon}
                      alt={suggestion.name}
                      boxSize="32px"
                      borderRadius="8px"
                      objectFit="contain"
                      flexShrink={0}
                    />
                  )}
                  <Text fontWeight={500} fontSize="15px" color={ff.ink}>
                    {suggestion.name}
                  </Text>
                </ListItem>
              ))}
            </List>
          )}
        </FormControl>
      </Box>
    </ModalShell>
  );
};

export default AddBrandModal;
